import type { CategoryIcon, StockRowDTO } from '@bifeai/shared';
import { Badge, Input, type Tone } from '@bifeai/ui';
import { Beef, Drumstick, Egg, Ham, LayoutGrid, List, Package, Sandwich, Search, ShoppingBasket, Snowflake, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { ViewMode } from '../lib/view-mode';

export const CATEGORY_ICON_COMPONENTS: Record<CategoryIcon, LucideIcon> = {
  beef: Beef,
  pork: Ham,
  chicken: Drumstick,
  sausage: Sandwich,
  egg: Egg,
  frozen: Snowflake,
  grocery: ShoppingBasket,
  generic: Package,
};

export function CategoryGlyph({ icon }: { icon: CategoryIcon | null }) {
  const Icon = CATEGORY_ICON_COMPONENTS[icon ?? 'generic'];
  return <Icon aria-hidden />;
}

/** Foto del producto o, si no tiene, el ícono de su categoría. */
export function ProductThumb({
  photoUrl,
  icon,
  name,
  size = 'md',
}: {
  photoUrl: string | null;
  icon: CategoryIcon | null;
  name: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`bf-thumb bf-thumb--${size}`}>
      {photoUrl && !failed ? (
        <img src={photoUrl} alt={name} loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <CategoryGlyph icon={icon} />
      )}
    </span>
  );
}

export function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div className="bf-segmented" role="group" aria-label="Forma de ver">
      <button type="button" aria-pressed={value === 'grid'} onClick={() => onChange('grid')} title="Ver en tarjetas" aria-label="Ver en tarjetas">
        <LayoutGrid aria-hidden />
      </button>
      <button type="button" aria-pressed={value === 'list'} onClick={() => onChange('list')} title="Ver en lista" aria-label="Ver en lista">
        <List aria-hidden />
      </button>
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="bf-search">
      <Search aria-hidden />
      <span className="bf-sr-only">{placeholder}</span>
      <Input type="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="bf-toolbar">{children}</div>;
}

export const STOCK_STATUS: Record<StockRowDTO['status'], { label: string; tone: Tone }> = {
  OK: { label: 'En stock', tone: 'success' },
  LOW: { label: 'Stock bajo', tone: 'warning' },
  OUT: { label: 'Sin stock', tone: 'neutral' },
  NEGATIVE: { label: 'Stock negativo', tone: 'danger' },
  EXPIRING: { label: 'Por vencer', tone: 'warning' },
  EXPIRED: { label: 'Vencido', tone: 'danger' },
};

export function StockStatusBadge({ status }: { status: StockRowDTO['status'] }) {
  const s = STOCK_STATUS[status];
  return (
    <Badge tone={s.tone} dot>
      {s.label}
    </Badge>
  );
}
